import React, { useEffect, useCallback, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import {
  useNavigate, useLocation, useSearchParams,
} from 'react-router-dom';
import { compose } from 'ramda';
import withSignUpDialog from 'components/hoc/withSignUpDialog';
import withNotification from 'components/hoc/withNotification';
import useJoinCompetition from 'hooks/useJoinCompetition';
import useIsJoined from 'hooks/useIsJoined';
import colors from 'styles/colors';
import LoadingButton from '@mui/lab/LoadingButton';
import { auth, useAuthState } from 'api/services/firebase';
import useAccountStore from 'store/accountStore';
import getCompetitionTiming from 'utils/competitionTiming';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const joinButtonStyles = {
  actionButton: {
    width: '112px',
    height: '40px',
    padding: '16px 20px',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '0 !important',
  },
  statusButton: {
    color: colors.text.secondary,
    fontWeight: '600',
    marginRight: '2px',
    border: `1px solid ${colors.border.border_10p}`,
    padding: '12px',
    fontSize: '14px',
    lineHeight: '14px',
    borderRadius: '100px',
    background: colors.background.button,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const TOOLTIP_MESSAGES = {
  teamMode: 'You cannot join as a team. Please switch off team mode from the top bar to join the competition',
  signUp: 'Please sign up to join',
  registrationEnd: 'Registration has ended',
  hasEnded: 'Hackathon has ended',
};

const tooltipTitle = (disabled, isJoined, user, registrationEnded, hasEnded) => {
  if (disabled && !isJoined) return TOOLTIP_MESSAGES.teamMode;
  if (!user && !registrationEnded && !hasEnded) return TOOLTIP_MESSAGES.signUp;
  if (hasEnded) return TOOLTIP_MESSAGES.hasEnded;
  if (registrationEnded) return TOOLTIP_MESSAGES.registrationEnd;
  return '';
};

const JoinedMessage = () => (
  <Typography sx={joinButtonStyles.statusButton} color={colors.success.main}>
    <CheckCircleOutlineIcon
      sx={{ fontSize: '14px', color: colors.success.light, marginRight: '10px' }}
    />
    Joined
  </Typography>
);

const RegistrationEndMessage = ({ styles }) => (
  <LoadingButton
    type="primary"
    variant="contained"
    disabled
    loading={false}
    loadingPosition="end"
    endIcon={<div />}
    sx={{ ...joinButtonStyles.actionButton, ...styles }}
  >
    Join
  </LoadingButton>
);

const JoinActionButton = ({
  disabled,
  isLoading,
  styles,
  handleJoinClick,
}) => (
  <LoadingButton
    type="primary"
    variant="contained"
    sx={{ ...styles, ...joinButtonStyles.actionButton }}
    disabled={disabled}
    loading={isLoading}
    loadingPosition="end"
    endIcon={<div />}
    onClick={(event) => {
      event.stopPropagation();
      handleJoinClick();
    }}
  >
    Join
  </LoadingButton>
);

const JoinButton = ({
  id,
  styles = {},
  showErrorNotification,
  competition,
  setHasJoinedLoading,
  openSignUpDialog,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isTeam = useAccountStore((state) => state.activeAccount.teamId);
  const [user] = useAuthState(auth);
  const { isJoined, isLoading: isJoinedLoading, isChecking } = useIsJoined(id);

  const { hasEnded, hasRegistrationEnded } = getCompetitionTiming(competition);

  // Separate state for join operation loading
  const [isJoinOperationLoading, setJoinOperationLoading] = useState(false);
  // Additional state to handle transition period
  const [isTransitioning, setIsTransitioning] = useState(false);

  const joinParamExists = searchParams.has('join');

  const navigateToCompetition = useCallback(() => {
    const competitionPath = `/hackathons/${id}`;
    if (location.pathname !== competitionPath) {
      navigate(competitionPath);
    }
  }, [id, location.pathname, navigate]);

  const { mutate } = useJoinCompetition(
    navigateToCompetition,
    (error) => showErrorNotification(error, undefined, 2000),
  );

  const handleUserJoin = useCallback(async () => {
    if (isJoined) return;
    setJoinOperationLoading(true);
    try {
      await mutate(id);
      // Add a minimum loading time of 1 second to ensure smooth transition
      await new Promise((resolve) => { setTimeout(resolve, 500); });
    } finally {
      setJoinOperationLoading(false);
      setHasJoinedLoading && setHasJoinedLoading(false);
    }
  }, [id, isJoined, mutate]);

  const handleGuestJoin = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('open', 'sign-up');
    params.set('back', `/hackathons/${id}?join=true`);
    navigate(`${location.pathname}?${params.toString()}`);
    if (searchParams.has('back')) {
      openSignUpDialog();
    }
  }, [id, location, navigate]);

  const handleJoinClick = useCallback(async () => {
    if (user) {
      await handleUserJoin();
    } else {
      handleGuestJoin();
    }
  }, [user, handleUserJoin, handleGuestJoin]);

  useEffect(() => {
    let transitionTimer;

    if (!isChecking && user && !isJoined && joinParamExists && !isJoinedLoading) {
      setIsTransitioning(true);
      handleJoinClick();
    }

    if (!isChecking && isJoined && joinParamExists) {
      // Add a small delay before removing the join param to ensure smooth transition
      transitionTimer = setTimeout(() => {
        setIsTransitioning(false);
        setHasJoinedLoading && setHasJoinedLoading(false);

        // Get the current search string without the leading ?
        const searchString = location.search.substring(1);

        // Split the search string into individual parameters
        const params = searchString.split('&').filter(Boolean);

        // Filter out the 'join' parameter, keeping all others
        const filteredParams = params.filter((param) => !param.startsWith('join='));

        // Create new search string
        const newSearch = filteredParams.length > 0 ? `?${filteredParams.join('&')}` : '';

        // Navigate to new path
        navigate(location.pathname + newSearch, { replace: true });
      }, 500);
    }

    return () => {
      if (transitionTimer) {
        clearTimeout(transitionTimer);
      }
    };
  }, [
    user,
    isJoined,
    isChecking,
    handleJoinClick,
    joinParamExists,
    isJoinedLoading,
    navigate,
    location.pathname,
  ]);
  useEffect(() => {
    if (isJoined) {
      setHasJoinedLoading && setHasJoinedLoading(false);
    }
  }, [isJoined, setHasJoinedLoading]);
  // Consolidate all loading states
  const showLoading = isChecking || isJoinedLoading || isJoinOperationLoading || isTransitioning;

  const isDisabled = isTeam || (user && showLoading);

  const renderButton = () => {
    if (isJoined) {
      return <JoinedMessage />;
    }

    if (hasRegistrationEnded || hasEnded) {
      return <RegistrationEndMessage styles={styles} />;
    }

    return (
      <JoinActionButton
        user={user}
        disabled={isDisabled}
        isLoading={!!(showLoading && user)}
        styles={styles}
        handleJoinClick={handleJoinClick}
      />
    );
  };

  return (
    <Tooltip title={tooltipTitle(isTeam, isJoined, user, hasRegistrationEnded, hasEnded)} arrow>
      <span>
        {renderButton()}
      </span>
    </Tooltip>
  );
};

export default compose(withNotification, withSignUpDialog)(JoinButton);
